import useClickOutside from "@admin/hooks/useClickOutside";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

export interface Option {
  value: number | string;
  label: string;
  color?: string;
}

interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  size?: "sm" | "md" | "lg";
  variant?: "standart" | "filled" | "outlined";
  defaultValue?: number | string;
  placeholder?: string;
  options?: Option[];
}

const dropdownStyles = cva("w-full relative", {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
    variant: {
      standart: "border rounded-md border-gray-300",
      filled: "text-gray-700 rounded-md bg-light border border-gray-300",
      outlined: "bg-transparent border-b-2 border-gray-500 focus:outline-none",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "standart",
  },
});

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  function (
    {
      size = "md",
      variant = "standart",
      placeholder,
      options,
      defaultValue,
      onChange,
      disabled = false,
      ...props
    }: SelectProps,
    ref,
  ) {
    const [selected, setSelected] = useState<Option | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const innerRef = useRef<HTMLSelectElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLSelectElement);

    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (defaultValue === undefined) return;
      const defaultOption = options?.find(
        (option) => option.value === defaultValue,
      );
      if (defaultOption && !selected) setSelected(defaultOption);
    }, [defaultValue, options, selected]);

    const toggleDropdown = () => {
      if (disabled) return;
      setIsOpen((prev) => !prev);
    };

    const handleSelect = useCallback(
      (option: Option) => {
        if (!innerRef || !innerRef.current) return;
        if (disabled) return;
        setSelected(option);
        setIsOpen(false);

        const event = new Event("change", { bubbles: true });
        innerRef.current.value = option.value.toString();
        innerRef.current.dispatchEvent(event);
      },
      [disabled, innerRef],
    );

    useClickOutside(wrapperRef, () => setIsOpen(false));
    const { t } = useTranslation();

    return (
      <>
        <select
          ref={innerRef}
          className="hidden"
          onChange={onChange}
          value={selected?.value}
          disabled={disabled}
          {...props}
        >
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div ref={wrapperRef} className={dropdownStyles({ size, variant })}>
          <button
            type="button"
            className={clsx(
              "w-full flex items-center justify-between",
              isOpen && "bg-gray-100",
              disabled && "opacity-50 cursor-not-allowed",
              size === "sm" && "px-2 py-1",
              size === "md" && "px-3 py-2",
              size === "lg" && "px-4 py-3",
            )}
            onClick={toggleDropdown}
            disabled={disabled}
          >
            {selected ? (
              <span
                style={{ color: selected.color ?? "inherit" }}
                className="text-ellipsis overflow-hidden whitespace-nowrap"
              >
                {selected.label}
              </span>
            ) : (
              <span>{placeholder || t("common.Select an option")}</span>
            )}
            <i className="ki-filled ki-down float-right ml-1 "></i>
          </button>
          {isOpen && !disabled && (
            <ul className="absolute w-full top-full left-0 bg-white rounded mt-1 max-h-60 overflow-auto z-10 shadow-lg">
              {options?.map((option) => (
                <li
                  key={option.value}
                  className={`p-2 hover:bg-gray-100 cursor-pointer ${
                    selected?.value === option.value ? "bg-gray-200" : ""
                  }`}
                  style={{ color: option.color ?? "inherit" }}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </>
    );
  },
);
